<template>
  <Menu>
    <template #label="{ toggle, isOpen }">
      <Backdrop :show="isOpen" class="z-50" @click="toggle" />
      <div class="flex justify-end">
        <Button
          :type="buttonType"
          :is-active="isActive"
          data-test-id="sorting-menu-toggle-button"
          @click="toggle"
        >
          <template #icon>
            <IconUpdown v-if="isMobileOrTablet || isActive" class="size-5" />
            <IconUpdownGray v-else class="size-5" />
          </template>
          {{ $t(`sorting_select.sort_by`) }}
        </Button>
      </div>
    </template>

    <template #menu-content="{ close }">
      <FadeInTransition>
        <FocusTrap>
          <div
            class="mt-5 bg-white pt-4 text-center shadow"
            @keydown.esc="close"
          >
            <SortingItemsList
              ref="listRef"
              :items="values"
              :selected="selectedSortName"
              class="w-full space-y-2 px-6"
              @click:item="close"
            />

            <AgsLink
              class="mt-6 w-full appearance-none justify-center gap-1 border-t py-3"
              size="base"
              :to="
                toLocalePath({
                  params: { ...$route.params },
                  query: { ...$route.query, sort: undefined, page: undefined },
                })
              "
            >
              <IconReset class="h-3" />
              {{ $t('sorting_select.reset') }}
            </AgsLink>
          </div>
        </FocusTrap>
      </FadeInTransition>
    </template>
  </Menu>
</template>

<script setup lang="ts">
import { FocusTrap } from 'focus-trap-vue'
import { SortingItemsList } from '#components'
import {
  OdoscopeSortName,
  type OdoscopeSortValue,
} from '../../utils/odoscopeSort'
import useOdoscopeSorting from '../../composables/useOdoscopeSorting'

defineProps<{
  values: OdoscopeSortValue[]
}>()

const { toLocalePath } = useRouteHelpers()
const { isMobileOrTablet } = useDeviceType()
const { selectedSortName } = useOdoscopeSorting()

const listRef = ref<InstanceType<typeof SortingItemsList> | null>(null)

const buttonType = computed(() =>
  !isMobileOrTablet.value ? 'muted' : 'tertiary',
)

const isActive = computed(
  () => selectedSortName.value !== OdoscopeSortName.LabelDesc,
)

watch(
  () => listRef.value,
  async (list) => {
    if (list) {
      await nextTick()
      const firstItem = list.$el?.querySelector(
        '[tabindex], button, a, input, [role="menuitem"]',
      )
      if (firstItem) firstItem.focus()
    }
  },
)
</script>
